/**
 * „Bearbeiten für" of the shell's settings list (I-2510): the layers the
 * session may edit, as scopes of `ConnectSettingsList` —
 *
 * - „Mein Profil": the session's own layer (its active profile);
 * - the account's default profile while the session works in another one —
 *   the other profiles show, but master writes a profile only while a
 *   session works in it;
 * - every appearance the session may edit („Design „Atelier““);
 * - what apps add (`settingsScopes`): the admin app offers the session's
 *   company and tenant to administrators, with a lock per field.
 */
import { computed, ref, shallowRef, type ComputedRef } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Router } from 'vue-router';
import {
  reloadAccountConfigs,
  type AccountConfigLayerRef,
  type ConnectAppModule,
  type ConnectSettingsAppearanceOption,
  type ConnectSettingsScope,
  type ConnectSettingsScopeContext,
  type ConnectSettingsScopeStore,
  CONNECT_INSTALLATION_LAYER,
} from '@blpde/connect';
import { getAppearanceScheme } from '@blpde/nx-tokens';
import { useShellUser } from '@/auth';
import { APPEARANCE_CONFIG_KEY } from '@/appearance/look';
import { useAppearances, type AppearanceEntry } from '@/appearance/appearances';
import { apps, type AppEntry, type AppId } from '@/integration';
import { companyLabel, sessionLayerName, tenantLabel, useSessionScope } from '@/scope';
import { askForPermissionOnPick } from '@/systemNotificationPermission';
import { fetchAccountProfiles, type AccountProfile } from './accountProfiles';
import { appearanceName } from './layerCaption';
import { createAppearanceLayerStore, createSessionLayerStore } from './settingsStores';
import { SUPPORT_CONFIG_KEY } from './connectSettingsSections';
import { installationSupportStore } from './supportInstallation';

/** Scope id of the session's own layer. */
export const SESSION_SCOPE_ID = 'profile';

/**
 * A store of one of the user's own layers (a profile, the account): picking
 * „Glocke + Systemhinweis" for the browser asks the browser's permission in
 * the same click, before the write — the pick is the gesture the browser
 * wants (`askForPermissionOnPick`).
 */
export function ownLayerStore(store: ConnectSettingsScopeStore): ConnectSettingsScopeStore {
  return {
    ...store,
    set(key, path, value) {
      void askForPermissionOnPick(key, path, value);
      store.set(key, path, value);
    },
  };
}

type Install = (router: Router, id: AppId) => Promise<ConnectAppModule>;

/** Further scopes of the apps that offer some (`settingsScopes: true` on their entry). */
async function appScopes(
  entries: readonly AppEntry[],
  router: Router,
  install: Install,
  context: ConnectSettingsScopeContext
): Promise<ConnectSettingsScope[]> {
  const lists = await Promise.all(
    entries
      .filter((entry) => entry.settingsScopes)
      .map(async (entry) => {
        try {
          const module = await install(router, entry.id);
          return (await module.settingsScopes?.(context)) ?? [];
        } catch (err) {
          console.warn('[settings] could not load the settings scopes of', entry.id, err);
          return [];
        }
      })
  );
  return lists.flat();
}

/**
 * The key `support` of the installation's layer is served on the public door
 * (the admin door's tenant layer is not reachable through the edge): this store
 * sends that key to the shared public store and every other key to `base`, so
 * the rows of the list and the remote support page use the same one.
 */
function withSupportKey(
  base: ConnectSettingsScopeStore | null,
  support: ConnectSettingsScopeStore
): ConnectSettingsScopeStore {
  const own = (key: string) => key === SUPPORT_CONFIG_KEY;
  return {
    load: async (keys) => {
      await Promise.all([
        base ? base.load(keys.filter((key) => !own(key))) : undefined,
        keys.some(own) ? support.load([SUPPORT_CONFIG_KEY]) : undefined,
      ]);
    },
    view: (key) => (own(key) ? support.view(key) : (base?.view(key) ?? null)),
    set: (key, path, value) => (own(key) ? support : base)?.set(key, path, value),
    lock: base?.lock?.bind(base),
    loading: computed(() => support.loading.value || (base?.loading.value ?? false)),
    error: computed(() => support.error.value ?? base?.error.value ?? null),
  };
}

export interface ShellSettingsScopes {
  scopes: ComputedRef<ConnectSettingsScope[]>;
  /** Reads the profiles, the appearances and the apps' scopes (the list works before). */
  load(): Promise<void>;
}

/**
 * The scopes of the settings page. `entitled` and `install` are the shell's
 * (which apps the account may use, the app loader).
 */
export function useShellSettingsScopes(options: {
  router: Router;
  install: Install;
  entitled: (id: AppId) => boolean;
  /** A hosted app obtains shell captions even inside its own i18n subtree. */
  translate?: (key: string, named?: Record<string, unknown>) => string;
}): ShellSettingsScopes {
  const { t: localT } = useI18n();
  const t = options.translate ?? localT;
  const { appearances, load: loadAppearances } = useAppearances();
  const sessionScope = useSessionScope();
  const profiles = shallowRef<AccountProfile[] | null>(null);
  const extra = shallowRef<ConnectSettingsScope[]>([]);
  const { user } = useShellUser();
  const accountId = ref<number>(user.value?.accountId ?? 0);

  // A new appearance can bring a layer of any key: read every key again.
  const sessionStore = ownLayerStore(
    createSessionLayerStore({
      t,
      afterWrite: (key) => (key === APPEARANCE_CONFIG_KEY ? reloadAccountConfigs() : undefined),
    })
  );
  const stores = new Map<string, ConnectSettingsScopeStore>();

  function storeOf(id: string, create: () => ConnectSettingsScopeStore): ConnectSettingsScopeStore {
    let store = stores.get(id);
    if (!store) {
      store = create();
      stores.set(id, store);
    }
    return store;
  }

  const activeProfile = computed(() => profiles.value?.find((profile) => profile.isActive) ?? null);
  const defaultProfile = computed(
    () => profiles.value?.find((profile) => profile.isDefault) ?? null
  );

  function nameOfAppearance(id: string): string {
    return appearanceName(t, id, appearances.value);
  }

  /** Names the captions use: the profiles', the appearances', the session's company and tenant. */
  function layerName(layer: AccountConfigLayerRef): string | undefined {
    switch (layer.subjectType) {
      case 'account':
        return (profiles.value?.length ?? 0) > 1 ? defaultProfile.value?.name : undefined;
      case 'account_profile':
        return profiles.value?.find((profile) => profile.id === layer.subjectId)?.name;
      case 'appearance':
        return nameOfAppearance(layer.appearance ?? String(layer.subjectId));
      default:
        return sessionLayerName(layer);
    }
  }

  /** What the profile's `appearance.active` offers: every appearance the session sees. */
  const appearanceOptions = computed<ConnectSettingsAppearanceOption[]>(() =>
    appearances.value.map((entry: AppearanceEntry) => {
      const scheme = entry.builtin ? getAppearanceScheme(entry.id) : undefined;
      const name = scheme ? t(scheme.labelKey) : entry.name;
      return {
        value: entry.id,
        label:
          entry.owner === 'tenant' || entry.owner === 'company'
            ? `${name} · ${t(`connect.settings.schemaDesigner.owner.${entry.owner}`)}`
            : name,
      };
    })
  );

  const profileScopes = computed<ConnectSettingsScope[]>(() => {
    const list = profiles.value ?? [];
    const active = activeProfile.value;
    const own: ConnectSettingsScope = {
      id: SESSION_SCOPE_ID,
      label:
        list.length > 1 && active
          ? t('connect.settingsList.scopes.myProfileNamed', { name: active.name })
          : t('connect.settingsList.scopes.myProfile'),
      target:
        active && !active.isDefault
          ? { subjectType: 'account_profile', subjectId: active.id }
          : { subjectType: 'account', subjectId: accountId.value },
      appearanceOptions,
      layerName,
      store: sessionStore,
    };
    const others = list
      .filter((profile) => !profile.isActive)
      .map((profile): ConnectSettingsScope => {
        if (profile.isDefault) {
          const target = {
            subjectType: 'account',
            subjectId: profile.accountID || accountId.value,
          };
          return {
            id: `profile:${profile.id}`,
            label: t('connect.settingsList.scopes.defaultProfile', { name: profile.name }),
            target,
            appearanceOptions,
            layerName,
            store: storeOf(`profile:${profile.id}`, () =>
              ownLayerStore(createSessionLayerStore({ t, target }))
            ),
          };
        }
        return {
          id: `profile:${profile.id}`,
          label: t('connect.settingsList.scopes.profile', { name: profile.name }),
          target: { subjectType: 'account_profile', subjectId: profile.id },
          disabled: t('connect.settingsList.scopes.profileInactive'),
          store: sessionStore,
        };
      });
    return [own, ...others];
  });

  const appearanceScopes = computed<ConnectSettingsScope[]>(() =>
    appearances.value
      .filter((entry) => entry.editable)
      .map((entry) => ({
        id: `appearance:${entry.id}`,
        label: t('connect.settingsList.scopes.appearance', { name: nameOfAppearance(entry.id) }),
        target: {
          subjectType: 'appearance',
          subjectId: Number(entry.id) || 0,
          appearance: entry.id,
        },
        // An appearance holds no selection of appearances (master refuses it).
        excludedKeys: [APPEARANCE_CONFIG_KEY],
        layerName,
        store: storeOf(`appearance:${entry.id}`, () => createAppearanceLayerStore(entry.id, t)),
      }))
  );

  // Remote support's standing access: the installation layer, on the public door,
  // for the holders of `master.support.approve` (userinfo's `support_approver`).
  const supportStore = computed(() =>
    user.value?.supportApprover ? installationSupportStore(t) : null
  );
  const installationScopes = computed<ConnectSettingsScope[]>(() => {
    const support = supportStore.value;
    if (!support) return extra.value;
    const isInstallation = (scope: ConnectSettingsScope) =>
      scope.target.subjectType === CONNECT_INSTALLATION_LAYER.subjectType &&
      scope.target.subjectId === CONNECT_INSTALLATION_LAYER.subjectId;
    const admin = extra.value.find(isInstallation);
    if (admin) {
      return extra.value.map((scope) =>
        scope === admin ? { ...scope, store: withSupportKey(scope.store, support) } : scope
      );
    }
    return [
      ...extra.value,
      {
        id: 'installation',
        label: t('connect.settingsList.scopes.installation'),
        target: { ...CONNECT_INSTALLATION_LAYER },
        locking: false,
        adminLayer: true,
        store: withSupportKey(null, support),
      },
    ];
  });

  const scopes = computed(() => [
    ...profileScopes.value,
    ...appearanceScopes.value,
    ...installationScopes.value,
  ]);

  async function load(): Promise<void> {
    const steps: Promise<unknown>[] = [
      fetchAccountProfiles()
        .then((list) => {
          profiles.value = list;
          const own = list.find((profile) => profile.isDefault);
          if (own?.accountID) accountId.value = own.accountID;
        })
        .catch(() => {
          profiles.value = null;
        }),
      loadAppearances(),
      sessionScope
        .load()
        .catch(() => undefined)
        .then(async () => {
          const active = sessionScope.active.value;
          extra.value = await appScopes(
            apps.filter((entry) => options.entitled(entry.id)),
            options.router,
            options.install,
            {
              tenant: active ? { id: active.tenant.id, name: tenantLabel(active.tenant) } : null,
              company: active
                ? { id: active.company.id, name: companyLabel(active.company) }
                : null,
              t: (key, named) => (named ? t(key, named) : t(key)),
            }
          );
        }),
    ];
    await Promise.all(steps);
  }

  return { scopes, load };
}
